import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Tooltip } from 'antd';
import { BellOutlined, EditOutlined } from '@ant-design/icons';
import { getCurrencyCode, getCurrencySymbol } from 'utils/currencyUtil';
import Can from 'config/Can';
import useTranslation from 'utils/hooks/useTranslation';
import useAmplitude from 'utils/hooks/useAmplitude';
import { toAnalyticsCurrency } from 'utils/walletUtil';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import AmountInput from './AmountInput';
import { formatWalletAmount } from './helper';

import styles from './style.module.scss';

const PRESETS = [500, 1000, 2000];
const { inputMethod: INPUT_METHODS, surface: SURFACES } = AMPLITUDE_WALLET_PROPERTY_VALUES;

// `canEdit` is false for frozen/closed wallets: the saved threshold stays visible, editing goes away.
// `onSave(value, inputMethod)`; `currency` is the wallet's own currency, for analytics.
const LowBalanceAlert = ({ threshold, onSave, canEdit = true, currency }) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const { currentCorporation } = useSelector(state => state.user);
  const country = currentCorporation?.country;
  const symbol = getCurrencySymbol(country);
  const format = value => formatWalletAmount(country, value);

  const [saved, setSaved] = useState(threshold);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(threshold);
  const [custom, setCustom] = useState('');

  // Once the parent's dashboard data refetches after a successful save, follow the server value.
  useEffect(() => {
    setSaved(threshold);
  }, [threshold]);

  const sendAmountSelected = (amount, inputMethod) =>
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertAmountSelected, {
      currency: toAnalyticsCurrency(currency),
      surface: SURFACES.walletEdit,
      amount,
      inputMethod,
    });

  const startEditing = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertEditLinkClicked);
    setDraft(saved);
    setCustom('');
    setIsEditing(true);
  };

  const handleSave = () => {
    const parsedCustom = parseFloat(custom);
    const isCustom = custom !== '' && !Number.isNaN(parsedCustom);
    const value = isCustom ? parsedCustom : draft;
    setSaved(value);
    setIsEditing(false);
    if (onSave) onSave(value, isCustom ? INPUT_METHODS.custom : INPUT_METHODS.preset);
  };

  const handleCancel = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertCancelButtonClicked);
    setIsEditing(false);
  };

  const handleCustomBlur = () => {
    const parsedCustom = parseFloat(custom);
    if (custom !== '' && !Number.isNaN(parsedCustom)) {
      sendAmountSelected(parsedCustom, INPUT_METHODS.custom);
    }
  };

  return (
    <div className={styles.card} data-testid="walletLowBalanceAlert">
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderIcon}>
          <BellOutlined />
        </span>
        {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.title')}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardCopy}>
          {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.copy')}
        </div>

        {canEdit && isEditing ? (
          <div data-testid="walletLowBalanceAlertEditPanel">
            <div className={styles.amountRow}>
              {PRESETS.map(preset => (
                <button
                  type="button"
                  key={preset}
                  className={classNames(styles.amountPreset, {
                    [styles.selected]: custom === '' && draft === preset,
                  })}
                  onClick={() => {
                    setDraft(preset);
                    setCustom('');
                    sendAmountSelected(preset, INPUT_METHODS.preset);
                  }}
                >
                  {format(preset)}
                </button>
              ))}
              <AmountInput
                currencyCode={getCurrencyCode(country)}
                symbol={symbol}
                label={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                )}
                value={custom}
                onChange={event => setCustom(event.target.value)}
                onBlur={handleCustomBlur}
                data-testid="walletLowBalanceAlertOtherAmount"
              />
            </div>
            <div className={styles.editActions}>
              <Can I="save" a="Wallet" passThrough>
                {allowed => (
                  <Tooltip
                    title={
                      allowed
                        ? null
                        : translateText(
                            'corporateAccounts.wallet.dashboard.lowBalanceAlert.saveDisabled',
                          )
                    }
                  >
                    <span>
                      <Button
                        type="primary"
                        className={styles.saveAlert}
                        onClick={handleSave}
                        disabled={!allowed}
                        data-testid="walletLowBalanceAlertSave"
                      >
                        {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.save')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
              <Button
                className={styles.cancelAlert}
                onClick={handleCancel}
                data-testid="walletLowBalanceAlertCancel"
              >
                {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.alertSavedRow}>
            <div className={styles.alertAmount}>{format(saved)}</div>
            {canEdit && (
              <span
                className={styles.editLink}
                onClick={startEditing}
                data-testid="walletLowBalanceAlertEdit"
              >
                <EditOutlined />
                {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.edit')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LowBalanceAlert;
